import { useContext, useEffect } from "react";
import {
  selectPersonnelsStore,
  selectRequestFormStore,
  selectUserData,
  useBoundStore,
} from "@/services/stores";
import { useFormContext } from "react-hook-form";
import { useRequestState } from "@/hooks";
import { AuthContext } from "@/guards/auth/auth-context";
import { selectApplicationStore } from "@/services/stores/selectors/applications";
import { assignIn, filter, find, map } from "lodash";
import {
  Application,
  requestApplications,
  RequestResponse,
} from "@/services/api";
import { logger } from "@/utils";

export const useRequiredDocuments = (
  referenceNumber: string | null,
  data?: RequestResponse | null,
) => {
  const { account } = useContext(AuthContext);

  const {
    isLoading,
    data: applicationsData,
    error,
    setSuccess,
    setError,
  } = useRequestState<Application[]>({ isLoading: true });

  const { applications, setApplications, setActiveApplication } = useBoundStore(
    selectApplicationStore,
  );

  const { requestFormData } = useBoundStore(selectRequestFormStore);
  const { selectedPersonnelMode } = useBoundStore(selectPersonnelsStore);
  const { user } = useBoundStore(selectUserData);

  const { trigger } = useFormContext();

  const getVisibleApplication = (applications: Application[]) =>
    filter(applications, application => application.Visible);

  const activeApplication =
    data?.Lookups?.ApplicationLookup?.Applications?.[0] || null;

  useEffect(() => {
    const controller = new AbortController();

    if (requestFormData && !applications) {
      const requestData = {
        email: account?.username ?? "",
        appRequest: {
          CompanyId: Number(requestFormData?.Companies?.Id) || 0,
          ReferenceNumber: referenceNumber,
          NoOfPersons: Number(requestFormData?.NumberOfPersonnel) || 0,
          NoOfVehicles: Number(requestFormData?.NumberOfVehicles) || 0,
          NoOfCameras: Number(requestFormData?.NumberOfCameras) || 0,
          NoOfVessels: Number(requestFormData?.NumberOfVessels) || 0,
          HasPersons:
            !!selectedPersonnelMode && !!requestFormData?.Personnels?.length,
          From: requestFormData?.From ?? "",
          To: requestFormData?.To ?? "",
          ContractorId: Number(user?.Contractor?.ContractorId) || 0,
        },
      };

      const subContractorId = requestFormData?.SubContractor?.SubContractorId;

      if (subContractorId) {
        assignIn(requestData.appRequest, {
          SubContractorId: Number(subContractorId),
        });
      }

      requestApplications(requestData, { signal: controller.signal })
        .then(response => {
          const applications = () => {
            const visibleApplication = getVisibleApplication(response);

            return map(visibleApplication, application => ({
              ...application,
              Value: application.ApplicationNameEn,
            }));
          };

          setApplications(applications());

          if (activeApplication) {
            const _activeApplication = find(applications(), {
              Id: activeApplication.Id,
            });

            setActiveApplication(
              _activeApplication || {
                ...activeApplication,
                Value: activeApplication.ApplicationNameEn,
              },
            );
          }

          setSuccess(applications());
        })
        .catch(error => {
          logger.error(error);

          setError(error);
        })
        .finally(async () => {
          await trigger();
        });
    }

    return () => {
      controller.abort();
    };
  }, [
    account,
    activeApplication,
    applications,
    referenceNumber,
    requestFormData,
    selectedPersonnelMode,
    setActiveApplication,
    setApplications,
    setError,
    setSuccess,
    trigger,
    user?.Contractor?.ContractorId,
    user?.Contractor?.SubContractorId,
  ]);

  return { applicationsData, isLoading, error };
};
